import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { Helper } from '../../components/ui/Helper';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { LifecycleStepper, NextActionCard, type LifecycleAction } from '../../components/ui/LifecycleStepper';
import { MoneyThread, type MoneyThreadStep } from '../../components/ui/MoneyThread';
import { quotesQueryOptions } from '../../lib/scadenziario';
import { fmtEur, fmtDate } from '../../lib/utils';
import { VISTA_LABEL, adminLinks } from '../../lib/adminRoutes';
import { METHOD_LABEL, type PayMethod } from '../admin/adminApi';

interface InvoiceFull {
  id: string;
  number: string | null;
  type: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  subtotal: string;
  vat: string;
  total: string;
  paymentMethod: string | null;
  sdiId: string | null;
  notes: string | null;
  ficDocumentId: number | null;
  ficUrl: string | null;
  ficPushedAt: string | null;
  ficSyncedAt: string | null;
  client: { id: string; legalName: string; shortName: string; vat: string | null; sdiCode: string | null; pec: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string }>;
}

interface FicConnectionStatus {
  connected: boolean;
  companyName: string | null;
}

/** Il tipo di documento con parole intere (era lo slug: "nota credito", "avviso pagamento"). */
const TIPO_FATTURA: Record<string, string> = {
  FATTURA: 'Fattura',
  NOTA_CREDITO: 'Nota di credito',
  PROFORMA: 'Pre-fattura',
  AVVISO_PAGAMENTO: 'Avviso di pagamento',
};

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const { data: inv, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<InvoiceFull>(`/invoices/${id}`),
    enabled: !!id,
    retry: false,
  });

  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicConnectionStatus>('/settings/integrations/fic/status'),
    refetchOnWindowFocus: false,
  });

  // Preventivi: stessa queryKey/fetch della pagina Contabilità (cache condivisa).
  // Servono per ricostruire il cross-ref preventivo → fattura del filo del denaro.
  const { data: quoteData } = useQuery({ ...quotesQueryOptions, enabled: !!id });

  // ──── Mutations
  const issue = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Fattura emessa con la numerazione interna', 'Stato aggiornato e numero assegnato.');
    },
    onError: (e: any) => toast.alert('Fattura non emessa', e?.message ?? 'Riprova'),
  });

  // Chiude tutte le rate aperte della fattura (oggi, bonifico): è la stessa
  // registrazione di "Segna incassata" in Fatture da incassare, fatta solo dentro
  // Masamune OS. Su FattureInCloud non si scrive.
  const markPaid = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/mark-paid`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      celebrate({ title: 'Fattura incassata', amount: Number(inv?.total ?? 0), subtitle: inv?.number ? `Fattura ${inv.number}` : undefined, tone: 'ok' });
    },
    onError: (e: any) => toast.alert('Fattura non segnata incassata', e?.message ?? 'Riprova'),
  });

  const updateNotes = useMutation({
    mutationFn: () => api.patch(`/invoices/${id}`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      setNotes('');
      setNotesOpen(false);
      toast.ok('Nota interna aggiornata');
    },
    onError: (e: any) => toast.alert('Nota interna non salvata', e?.message ?? 'Riprova'),
  });

  const pushToFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/push-to-fic`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      // Pushare a FIC cambia status BOZZA → EMESSA: il KPI "Fatturato YTD" della
      // dashboard considera EMESSA/INVIATA/PAGATA, quindi va invalidato.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Fattura emessa su FattureInCloud', 'Il numero ufficiale lo ha dato FattureInCloud.');
    },
    onError: (e: any) => toast.alert('Fattura non emessa su FattureInCloud', e?.message ?? 'Riprova'),
  });

  const syncFromFic = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/sync-from-fic`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.ok('Fattura aggiornata da FattureInCloud');
    },
    onError: (e: any) => toast.alert('Fattura non aggiornata da FattureInCloud', e?.message ?? 'Riprova'),
  });

  const sendEmailFic = useMutation({
    mutationFn: (payload: { to?: string; cc?: string; subject?: string; body?: string }) =>
      api.post(`/invoices/${id}/send-email-fic`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      setEmailModalOpen(false);
      toast.ok('Fattura mandata al cliente per email', 'La mail è partita da FattureInCloud.');
    },
    onError: (e: any) => toast.alert('Fattura non mandata al cliente', e?.message ?? 'Riprova'),
  });

  const sendSdi = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/send-sdi`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      toast.ok("Fattura mandata all'Agenzia delle Entrate");
    },
    onError: (e: any) => toast.alert("Fattura non mandata all'Agenzia delle Entrate", e?.message ?? 'Riprova'),
  });

  // ──── States
  if (!id) return <Navigate to={adminLinks.documenti()} replace />;
  if (isError) return (
    <div className="page invoice-detail-page">
      <Breadcrumb items={[{ label: VISTA_LABEL.fatture.documenti, to: adminLinks.documenti() }, { label: 'Fattura' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Fattura non trovata</h1>
        <div className="mute mt-2">Questa fattura non esiste o è stata cancellata.</div>
      </div>
    </div>
  );
  if (isLoading || !inv) return <DetailSkeleton header="chips" />;

  const ficConnected = !!ficStatus?.connected;
  const onFic = !!inv.ficDocumentId;
  const isDraft = inv.status === 'BOZZA';
  const isPaid = inv.status === 'PAGATA';
  const isCancelled = inv.status === 'ANNULLATA';

  // Calcola lo "stage" lifecycle: 0=Bozza · 1=Emessa · 2=Inviata · 3=Pagata
  const stageIdx = (() => {
    if (isDraft) return 0;
    if (isPaid) return 3;
    if (inv.status === 'INVIATA' || inv.status === 'SCADUTA') return 2;
    return 1; // EMESSA
  })();

  // ──── Filo del denaro: Preventivo → Progetto → Fattura → Incasso
  // Il preventivo si ricostruisce dalla cache quotes (convertedToInvoice → questa fattura).
  const quoteRef = quoteData?.items.find((q) => q.convertedToInvoice?.id === inv.id) ?? null;
  const threadSteps: MoneyThreadStep[] = [];
  if (quoteRef) threadSteps.push({ key: 'preventivo', label: 'Preventivo', to: `/quotes/${quoteRef.id}`, state: 'done' });
  if (inv.project) threadSteps.push({ key: 'progetto', label: 'Progetto', sub: inv.project.code, to: `/projects/${inv.project.id}`, state: 'done' });
  // Senza alcun cross-ref oltre la fattura stessa il filo non ha senso: non si renderizza.
  const hasThread = threadSteps.length > 0;
  threadSteps.push({ key: 'fattura', label: 'Fattura', sub: `€ ${fmtEur(inv.total)}`, state: 'current' });
  if (!isCancelled) threadSteps.push(
    isPaid
      ? { key: 'incasso', label: 'Incasso', sub: inv.paidDate ? fmtDate(inv.paidDate) : undefined, state: 'done' }
      : { key: 'incasso', label: 'Incasso', sub: inv.dueDate ? `atteso ${fmtDate(inv.dueDate)}` : 'atteso', state: 'future' },
  );

  // Next-action contestuale
  const nextAction = computeNextAction({
    status: inv.status, onFic, ficConnected,
    onPushFic: () => pushToFic.mutate(),
    onIssueLocal: () => issue.mutate(),
    onSendEmail: () => setEmailModalOpen(true),
    onMarkPaid: () => markPaid.mutate(),
    isPushing: pushToFic.isPending,
    isIssuing: issue.isPending,
    isMarkingPaid: markPaid.isPending,
  });

  // Menu "Azioni": le stesse voci del vecchio dropdown locale, ora su ActionsMenu
  // condiviso. `excludeKey` nasconde la voce già promossa nella NextActionCard.
  const canMarkPaid = ['EMESSA', 'INVIATA', 'SCADUTA'].includes(inv.status);
  const actionItems: ActionItem[] = [
    { key: 'sync-fic',   label: 'Aggiorna la fattura da FattureInCloud',  icon: 'refresh', onClick: () => syncFromFic.mutate(), hidden: !onFic },
    { key: 'send-email', label: 'Manda la fattura al cliente per email', icon: 'mail',    onClick: () => setEmailModalOpen(true), hidden: !onFic },
    {
      key: 'send-sdi', label: "Manda all'Agenzia delle Entrate (SdI)", icon: 'send', hidden: !onFic,
      onClick: async () => {
        if (await confirmDialog({
          title: "Mandare la fattura all'Agenzia delle Entrate?",
          body: 'Parte verso il Sistema di Interscambio (SdI) e non si può ritirare.',
          danger: true,
          confirmLabel: 'Manda la fattura',
        })) sendSdi.mutate();
      },
    },
    { key: 'open-fic-pdf', label: 'Apri il PDF su FattureInCloud',   icon: 'link',  onClick: () => window.open(inv.ficUrl!, '_blank'), hidden: !inv.ficUrl },
    { key: 'issue',        label: 'Emetti la fattura qui',   icon: 'send',  onClick: () => issue.mutate(), hidden: !(isDraft && !ficConnected) },
    { key: 'mark-paid',    label: 'Segna incassata oggi, con bonifico', icon: 'check', onClick: () => markPaid.mutate(), hidden: !canMarkPaid },
    {
      key: 'print', label: 'Stampa la fattura o salvala in PDF', icon: 'export',
      onClick: () => {
        if (typeof window === 'undefined') return;
        window.print();
        toast.info('Stampa avviata', 'Usa "Salva come PDF" dal dialogo di stampa per esportare il documento.');
      },
    },
  ];

  return (
    <div className="page">
      <Breadcrumb items={[{ label: VISTA_LABEL.fatture.documenti, to: adminLinks.documenti() }, { label: inv?.number ?? 'Bozza' }]} />

      {/* RIFACIMENTO EDITORIALE: solo l'header — titolo grande e leggero, hairline aurora */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il numero, tronca se lungo */}
          <h1 className="ed-title truncate">{inv.number ?? 'Bozza · senza numero'}</h1>
          {/* L3 meta-indice: tipo · documento elettronico, una riga mute */}
          <div className="detail-meta mt-2 truncate">
            <span>{TIPO_FATTURA[inv.type] ?? inv.type}</span>
            {inv.sdiId && <><span className="mute-2">·</span><span><span className="mute-2 mr-1.5">Numero SdI</span><span className="v mono">{inv.sdiId}</span></span></>}
            {onFic && (
              <><span className="mute-2">·</span>
              <span>
                <span className="mute-2 mr-1.5">Su FattureInCloud</span>
                <span className="v mono">#{inv.ficDocumentId}</span>
                {inv.ficUrl && <a href={inv.ficUrl} target="_blank" rel="noreferrer" className="v underline ml-1.5 m-hit">apri il documento</a>}
              </span></>
            )}
          </div>
          {/* L4 più arretrata: cliente + progetto, label mute, valori --text peso 400.
              Niente P.IVA qui: vive già nella card Cliente (zero info duplicate). */}
          <div className="detail-meta mt-1 truncate">
            <span><span className="mute-2 mr-1.5">Cliente</span><span className="v">{inv.client.legalName}</span></span>
            {inv.project && (
              <><span className="mute-2">·</span>
              <span><span className="mute-2 mr-1.5">Progetto</span><a className="v mono hover:underline cursor-pointer" onClick={() => navigate(`/projects/${inv.project!.id}`)}>{inv.project.code}</a></span></>
            )}
          </div>
          {/* Stato/tipo che "urla" (solo nota di credito): riga dedicata SOTTO, mai in cima */}
          {inv.type === 'NOTA_CREDITO' && (
            <div className="flex gap-1.5 mt-2.5 items-center">
              <Chip tone="warn">{TIPO_FATTURA[inv.type] ?? inv.type}</Chip>
            </div>
          )}
        </div>

        <div className="flex gap-2 pb-1 d-only">
          <ActionsMenu
            variant="ghost"
            label="Azioni sulla fattura"
            items={actionItems}
            excludeKey={nextAction?.key}
            disabled={syncFromFic.isPending || markPaid.isPending || sendEmailFic.isPending || sendSdi.isPending || issue.isPending}
          />
        </div>
      </div>

      {/* Filo del denaro sopra il ciclo di vita (stesso ritmo della pagina preventivo) */}
      {hasThread && (
        <div className="mb-5">
          <MoneyThread steps={threadSteps} />
        </div>
      )}

      {/* Lifecycle stepper (condiviso) — ANNULLATA: banner, come pattern "rejected" dei preventivi */}
      {isCancelled ? (
        <div className="card mb-6">
          <div className="card-bd py-4 flex items-center gap-3 text-alert">
            <Icon name="x" size={18} />
            <strong>Fattura annullata</strong>
            <span className="mute text-[13px]">non si può più modificare</span>
          </div>
        </div>
      ) : (
        <LifecycleStepper
          steps={[
            { key: 'bozza',   label: 'Bozza',   icon: 'file',  sub: 'In lavorazione' },
            { key: 'emessa',  label: 'Emessa',  icon: 'send',  sub: onFic ? 'Su FattureInCloud' : 'Numerata' },
            { key: 'inviata', label: 'Inviata', icon: 'mail',  sub: inv.status === 'SCADUTA' ? 'In ritardo' : 'Al cliente' },
            { key: 'pagata',  label: 'Pagata',  icon: 'check', sub: 'Saldata' },
          ]}
          currentIdx={stageIdx}
          activeTone={inv.status === 'SCADUTA' ? 'alert' : undefined}
          maxWidth={720}
        />
      )}

      {/* CTA grande contestuale */}
      {nextAction && (
        <div className="mb-6">
          <NextActionCard action={nextAction} />
        </div>
      )}

      <div className="m-desk-note m-only mb-5" role="note">
        <Icon name="help" size={14} />
        <span><strong>Stampa, sincronizzazione e invio allo SdI</strong> si fanno dal computer; sul telefono restano l'azione successiva, l'incasso e le note.</span>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="min-w-0">
          {/* Righe documento: tabella nuda sul foglio — kicker + conteggio, niente gabbia */}
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Righe della fattura <span className="mono">· {inv.lines.length}</span></span>
            </div>
            <table className="tbl tbl--ed tbl--card tbl--static">
              <thead><tr><th>Descrizione</th><th className="num">Quantità</th><th className="num">Prezzo unitario</th><th className="num">IVA</th><th className="num">Totale con IVA</th></tr></thead>
              <tbody>
                {inv.lines.map((l) => {
                  const qty = Number(l.quantity) || 0;
                  const price = Number(l.unitPrice) || 0;
                  const vatRate = Number(l.vatRate) || 0;
                  const lineTotal = qty * price * (1 + vatRate / 100);
                  return (
                    <tr key={l.id}>
                      <td data-m="hd">{l.description}</td>
                      <td data-m="key" data-l="Quantità" className="num mono">{qty}</td>
                      <td data-m="key" data-l="Prezzo unitario" className="num mono">€ {fmtEur(price)}</td>
                      <td data-m="key" data-l="IVA" className="num mono mute">{vatRate}%</td>
                      <td data-m="key" data-l="Totale con IVA" className="num mono font-semibold">€ {fmtEur(lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Totali in calce a destra, come su carta: hairline, niente gabbia.
                pr-5 allinea i numeri al padding (20px) dell'ultima colonna. */}
            <div className="flex justify-end pr-5 mt-1">
              <div className="w-[300px] max-w-full text-[13px]">
                <div className="flex justify-between items-baseline py-2 border-b border-line">
                  <span className="mute">Imponibile</span>
                  <span className="mono">€ {fmtEur(inv.subtotal)}</span>
                </div>
                <div className="flex justify-between items-baseline py-2 border-b border-line">
                  <span className="mute">IVA</span>
                  <span className="mono">€ {fmtEur(inv.vat)}</span>
                </div>
                <div className="flex justify-between items-baseline pt-3">
                  <span className="t-micro">Totale con IVA</span>
                  {/* Il totale è LA metrica della pagina: viola, mai semaforo */}
                  <span className="mono tbl-metric accent text-[20px]">€ {fmtEur(inv.total)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="ed-divider" />

          {/* Blocco nudo: kicker + contenuto; il composer inline conserva i suoi input */}
          <div>
            <div className="data-hd">
              <span className="t-micro">Note interne</span>
            </div>
            <div>
              {inv.notes ? (
                <div className="whitespace-pre-wrap mb-3">{inv.notes}</div>
              ) : (
                <div className="mute text-[13px] mb-3">Nessuna nota interna</div>
              )}
              {notesOpen ? (
                <>
                  <textarea className="inp" rows={3} placeholder="Aggiungi una nota interna…" value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
                  <div className="flex gap-2 mt-2">
                    <button className="btn btn-sm" disabled={!notes.trim() || updateNotes.isPending} onClick={() => updateNotes.mutate()}>
                      Salva la nota interna
                    </button>
                    <button className="btn btn-sm" onClick={() => { setNotesOpen(false); setNotes(''); }}>
                      Annulla
                    </button>
                  </div>
                </>
              ) : (
                <button className="btn btn-sm" onClick={() => { setNotes(inv.notes ?? ''); setNotesOpen(true); }}>
                  <Icon name={inv.notes ? 'file' : 'plus'} size={12} /> {inv.notes ? 'Modifica la nota interna' : 'Aggiungi una nota interna'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="min-w-0">
          {/* Anagrafica nuda: kicker + chiave-valore hairline, azione nel kicker */}
          <div>
            <div className="data-hd">
              <span className="t-micro">Cliente</span>
              <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/clients/${inv.client.id}`)}>
                Apri la scheda del cliente <Icon name="arrowRight" size={10} />
              </button>
            </div>
            <div className="ed-kv">
              <InfoLine label="Ragione sociale" value={inv.client.legalName} />
              {inv.client.vat && <InfoLine label="P.IVA" value={inv.client.vat} mono />}
              {inv.client.sdiCode
                ? <InfoLine label={<>SDI <Helper text="Codice destinatario di 7 caratteri per la fatturazione elettronica B2B. Se il cliente non ce l'ha, si usa la PEC o il codice 0000000." /></>} value={inv.client.sdiCode} mono />
                : inv.client.pec
                  ? <InfoLine label={<>PEC <Helper text="Posta elettronica certificata: alternativa al codice SDI per recapito di fatture elettroniche." /></>} value={inv.client.pec} mono />
                  : <InfoLine label="Codice SDI o PEC" value={<span className="text-warn">Mancano: servono per mandare la fattura elettronica</span>} />
              }
            </div>
          </div>

          <div className="ed-divider" />

          {/* Blocco nudo: kicker + chiave-valore hairline */}
          <div>
            <div className="data-hd">
              <span className="t-micro">Date e pagamento</span>
            </div>
            <div className="ed-kv">
              <InfoLine label="Emessa il"       value={fmtDate(inv.issueDate)} mono />
              <InfoLine label="Da pagare entro" value={fmtDate(inv.dueDate)} mono />
              <InfoLine label="Pagata il"       value={fmtDate(inv.paidDate)} mono />
              <InfoLine label="Pagata con"      value={inv.paymentMethod ? METHOD_LABEL[inv.paymentMethod as PayMethod] ?? inv.paymentMethod : '—'} />
            </div>
          </div>

          {!ficConnected && (
            /* mt-7 = stesso respiro (28px) dell'ed-divider tra i blocchi nudi */
            <div className="mt-7">
              <CoachCard
                tone="info"
                icon="link"
                title="Connetti FattureInCloud"
                body="Da Masamune OS puoi emettere fatture ufficiali direttamente tramite FattureInCloud. Connettilo per attivare la fatturazione elettronica e SdI."
                cta={{ label: 'Vai a Impostazioni', onClick: () => navigate('/settings') }}
              />
            </div>
          )}
        </div>
      </div>

      {emailModalOpen && (
        <SendEmailModal
          defaultTo=""
          invoiceNumber={inv.number ?? 'documento'}
          onClose={() => setEmailModalOpen(false)}
          onSend={(payload) => sendEmailFic.mutate(payload)}
          sending={sendEmailFic.isPending}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// NEXT-ACTION CONTESTUALE
// `key` mappa l'azione su una voce del menu Azioni (excludeKey su ActionsMenu),
// così l'azione promossa nella NextActionCard non appare due volte.
// ════════════════════════════════════════════════════════════════════
type NextAction = LifecycleAction & { key: string };

function computeNextAction(args: {
  status: string;
  onFic: boolean;
  ficConnected: boolean;
  onPushFic: () => void;
  onIssueLocal: () => void;
  onSendEmail: () => void;
  onMarkPaid: () => void;
  isPushing: boolean;
  isIssuing: boolean;
  isMarkingPaid: boolean;
}): NextAction | null {
  const { status, onFic, ficConnected } = args;

  if (status === 'BOZZA') {
    if (ficConnected) return {
      key: 'push-fic',
      title: 'Fattura pronta per essere emessa',
      body: 'Manda la fattura a FattureInCloud: le darà il numero ufficiale e preparerà il PDF.',
      cta: 'Emetti su FattureInCloud',
      icon: 'send',
      tone: 'primary',
      onClick: args.onPushFic,
      loading: args.isPushing,
    };
    return {
      key: 'issue',
      title: 'Fattura pronta per essere emessa',
      body: 'FattureInCloud non è collegato: la fattura avrà la numerazione interna di Masamune OS. Per il numero ufficiale collega FattureInCloud dalle Impostazioni.',
      cta: 'Emetti la fattura qui',
      icon: 'send',
      tone: 'warn',
      onClick: args.onIssueLocal,
      loading: args.isIssuing,
    };
  }

  if (status === 'EMESSA' && onFic) return {
    key: 'send-email',
    title: 'Manda la fattura al cliente',
    body: 'FattureInCloud invierà l\'email con il PDF allegato al contatto principale del cliente.',
    cta: 'Manda la fattura al cliente',
    icon: 'mail',
    tone: 'info',
    onClick: args.onSendEmail,
  };

  // "Segna incassata" registra e basta: non muove soldi e non scrive su
  // FattureInCloud. Qui chiude tutte le rate aperte, con la data di oggi.
  if ((status === 'EMESSA' || status === 'INVIATA') && !onFic) return {
    key: 'mark-paid',
    title: 'Il cliente ha pagato?',
    body: 'Quando il cliente paga, segna la fattura incassata: si chiude in Masamune OS con la data di oggi.',
    cta: 'Segna incassata',
    icon: 'check',
    tone: 'ok',
    onClick: args.onMarkPaid,
    loading: args.isMarkingPaid,
  };

  if (status === 'INVIATA' || status === 'EMESSA') return {
    key: 'mark-paid',
    title: 'Fattura in attesa del pagamento del cliente',
    body: 'Quando il cliente paga, segna la fattura incassata qui, oppure aggiorna la fattura da FattureInCloud per riprendere il pagamento da lì.',
    cta: 'Segna incassata',
    icon: 'check',
    tone: 'ok',
    onClick: args.onMarkPaid,
    loading: args.isMarkingPaid,
  };

  if (status === 'SCADUTA') return {
    key: 'send-email',
    title: 'Fattura in ritardo col pagamento: mandi un sollecito?',
    body: 'Il cliente non ha ancora pagato. Puoi rimandargli la fattura per email o sentirlo direttamente.',
    cta: 'Rimanda la fattura al cliente',
    icon: 'mail',
    tone: 'warn',
    onClick: args.onSendEmail,
  };

  return null;
}

// ════════════════════════════════════════════════════════════════════
// COACH CARD (suggerimento contestuale)
// ════════════════════════════════════════════════════════════════════
function CoachCard({ tone, icon, title, body, cta }: {
  tone: 'info' | 'warn' | 'ok';
  icon: IconName;
  title: string;
  body: string;
  cta?: { label: string; onClick: () => void };
}) {
  // 'info' = suggerimento neutro → tinta accento viola-soft (niente blu decorativo);
  // 'warn'/'ok' restano semantici (attenzione/positivo).
  const bgClass = tone === 'warn' ? 'bg-warn-bg text-warn' : tone === 'ok' ? 'bg-ok-bg text-ok' : '';
  const softAccent = tone === 'info'
    ? {
        background: 'color-mix(in oklab, var(--accent) 10%, var(--surface))',
        border: '1px solid color-mix(in oklab, var(--accent) 20%, transparent)',
        color: 'color-mix(in oklab, var(--accent) 72%, var(--ink))',
      }
    : undefined;
  return (
    <div className={`p-4 rounded-[var(--r-card)] ${bgClass}`} style={softAccent}>
      <div className="flex items-start gap-2.5 mb-2">
        <Icon name={icon} size={14} className="mt-0.5 shrink-0" />
        <strong className="text-[13px]">{title}</strong>
      </div>
      <div className="text-[13px] leading-[1.5] mb-3" style={{ opacity: 0.9 }}>{body}</div>
      {cta && (
        <button className="btn btn-sm" onClick={cta.onClick}>
          {cta.label} <Icon name="arrowRight" size={10} />
        </button>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SEND EMAIL MODAL (overlay leggero)
// ════════════════════════════════════════════════════════════════════
function SendEmailModal({ defaultTo, invoiceNumber, onClose, onSend, sending }: {
  defaultTo: string;
  invoiceNumber: string;
  onClose: () => void;
  onSend: (payload: { to?: string; cc?: string; subject?: string; body?: string }) => void;
  sending: boolean;
}) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`Fattura ${invoiceNumber}`);
  const [body, setBody] = useState('');

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 invoice-email-overlay" onClick={onClose}>
      <div className="bg-white rounded-[var(--r-card)] border border-line shadow-lg w-[460px] max-w-[92vw] invoice-email-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Spedisci la fattura">
        <div className="p-4 border-b border-line flex items-center justify-between invoice-email-hd">
          <div>
            <div className="t-micro">Invia al cliente · via FattureInCloud</div>
            <div className="t-h3 mt-0.5">Spedisci la fattura</div>
          </div>
          <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px] invoice-email-bd">
          <div>
            <label className="field-label">Destinatario</label>
            <input className="inp" placeholder="Lascia vuoto per usare il contatto primario del cliente" value={to} onChange={(e) => setTo(e.target.value)} />
            <div className="field-hint">Se lo lasci vuoto, FattureInCloud usa la mail del contatto principale.</div>
          </div>
          <div>
            <label className="field-label">CC</label>
            <input className="inp" placeholder="optional" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Oggetto</label>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Messaggio</label>
            <textarea className="inp" rows={4} placeholder="Lascia vuoto per usare il testo predefinito di FattureInCloud" value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t border-line flex gap-2 invoice-email-ft">
          <button className="btn" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={sending}
            onClick={() => onSend({
              to: to.trim() || undefined,
              cc: cc.trim() || undefined,
              subject: subject.trim() || undefined,
              body: body.trim() || undefined,
            })}
          >
            <Icon name="send" size={12} /> {sending ? 'Invio in corso…' : 'Manda la fattura'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// UTILS
// ════════════════════════════════════════════════════════════════════
/* Riga chiave-valore NUDA (.ed-kv-row): label maiuscola .k, valore .v, hairline tra le righe.
   Va sempre usata dentro un wrapper .ed-kv (l'ultima riga perde l'hairline). */
function InfoLine({ label, value, mono }: { label: React.ReactNode; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="ed-kv-row">
      <div className="k">{label}</div>
      <div className={`v ${mono ? 'mono' : ''}`}>{value}</div>
    </div>
  );
}
